import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { colmenasAPI, estadoAPI, eventosAPI, varroaAPI, resumenAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

const SEMAFORO = {
  verde: { bg: '#E8F5EC', color: '#4A7C59', icon: '🟢', labelKey: 'estado.semOk' },
  amarillo: { bg: '#FDF0DC', color: '#C97820', icon: '🟡', labelKey: 'estado.semAviso' },
  rojo: { bg: '#FDECEA', color: '#C0392B', icon: '🔴', labelKey: 'estado.semAlerta' },
};

const CAT_ORDER = ['alerta', 'general', 'info'];
const CAT_COLORS = {
  alerta: { bg: '#FDECEA', color: '#C0392B', border: '#F5C6CB' },
  general: { bg: '#F0EDE9', color: '#5C3D2E', border: '#E0D8CF' },
  info: { bg: '#E8F5EC', color: '#4A7C59', border: '#C3E6CB' },
};

const NIVEL_COLORS = {
  ok: { bg: '#E8F5EC', color: '#4A7C59' },
  alerta: { bg: '#FDF0DC', color: '#C97820' },
  critico: { bg: '#FDECEA', color: '#C0392B' },
};
const nivelColor = (nivel) => NIVEL_COLORS[nivel] || { bg: '#F0EDE9', color: '#8B6355' };

// Tipos que tienen bloque propio en la ficha: no se repiten en el historial.
const TIPOS_CON_BLOQUE = 'varroa,tratamiento,cosecha';

export default function Colmena() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'es';

  const TIPO_INFO = {
    inspeccion:   { icon: '🔍', color: '#4A7C59', label: t('timeline.typeInspeccion') },
    alimentacion: { icon: '🍯', color: '#E8922A', label: t('timeline.typeAlimentacion') },
    alza:         { icon: '📦', color: '#6B4FA0', label: t('timeline.typeAlza') },
    nota:         { icon: '📝', color: '#5C3D2E', label: t('timeline.typeNota') },
    otro:         { icon: '⚙️', color: '#8B6355', label: t('timeline.typeOtro') },
    tarea:        { icon: '✓',  color: '#4A7C59', label: t('timeline.typeTarea') },
  };

  const [colmena, setColmena] = useState(null);
  const [estados, setEstados] = useState([]);
  const [varroaSerie, setVarroaSerie] = useState([]);
  const [produccion, setProduccion] = useState(null);
  const [tratamientos, setTratamientos] = useState([]);
  const [tratTruncado, setTratTruncado] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noEncontrada, setNoEncontrada] = useState(false);

  const fmtFecha = useCallback(
    (iso) => new Date(iso).toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' }),
    [lang]
  );

  // Fechas sin hora (YYYY-MM-DD): se interpretan en local para que no se
  // desplacen un día en husos horarios negativos.
  const fmtFechaSolo = useCallback(
    (iso) => {
      const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
      if (!y || !m || !d) return iso;
      return new Date(y, m - 1, d).toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
    },
    [lang]
  );

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setNoEncontrada(false);
    colmenasAPI.get(id)
      .then(async (r) => {
        if (cancelado) return;
        setColmena(r.data);
        const [rEstados, rVarroa, rProd, rTrat, rHist] = await Promise.allSettled([
          estadoAPI.get(id),
          varroaAPI.historico(id),
          resumenAPI.produccion(),
          eventosAPI.list({ colmena_id: id, tipo: 'tratamiento' }),
          colmenasAPI.timeline(id, { limit: 10, excluir: TIPOS_CON_BLOQUE }),
        ]);
        if (cancelado) return;
        if (rEstados.status === 'fulfilled') setEstados(rEstados.value.data);
        if (rVarroa.status === 'fulfilled') setVarroaSerie(rVarroa.value.data);
        if (rProd.status === 'fulfilled') setProduccion(rProd.value.data);
        if (rTrat.status === 'fulfilled') {
          setTratamientos(rTrat.value.data);
          setTratTruncado(rTrat.value.headers?.['x-eventos-truncados'] === '1');
        }
        if (rHist.status === 'fulfilled') setHistorial(rHist.value.data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelado) return;
        setNoEncontrada(true);
        setLoading(false);
      });
    return () => { cancelado = true; };
  }, [id]);

  const toggleEstado = async (key, currentVal) => {
    try {
      await estadoAPI.update(id, key, !currentVal);
      const [rEstados, rColmena] = await Promise.all([estadoAPI.get(id), colmenasAPI.get(id)]);
      setEstados(rEstados.data);
      setColmena(rColmena.data);
    } catch (err) {
      console.error('Error toggling estado:', err);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div>;
  }

  if (noEncontrada || !colmena) {
    return (
      <div className="empty-state">
        <div className="icon">🐝</div>
        <h3>{t('ficha.notFoundTitle')}</h3>
        <p>{t('ficha.notFoundMsg')}</p>
        <Link to="/colmenas" className="btn btn-primary">{t('ficha.backToHives')}</Link>
      </div>
    );
  }

  const sem = SEMAFORO[colmena.semaforo || 'verde'];
  const razones = colmena.alertas_activas || [];
  const grouped = CAT_ORDER.reduce((acc, cat) => {
    acc[cat] = estados.filter(e => e.categoria === cat);
    return acc;
  }, {});

  const ultimoVarroa = varroaSerie.length > 0 ? varroaSerie[varroaSerie.length - 1] : null;
  const anterioresVarroa = varroaSerie.slice(0, -1).slice(-8).reverse();
  const maxVarroa = Math.max(...varroaSerie.map(v => v.valor || 0), 1);

  const prodColmena = produccion?.colmenas?.find(c => c.colmena_id === colmena.id);
  const aniosProd = prodColmena
    ? Object.keys(prodColmena.por_anio).map(Number).sort((a, b) => b - a)
    : [];

  const unidadVarroa = (u) => (u === 'pct' ? '%' : t('varroa.unidadAcarosDia'));

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* 1. Cabecera */}
      <div style={S.card}>
        <div style={S.headerRow}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={S.title}>🐝 {colmena.nombre}</h1>
            <div style={S.metaRow}>
              {colmena.numero && <span style={S.metaChip}>#{colmena.numero}</span>}
              {colmena.tipo && (
                <span style={S.metaChip}>
                  {t(`colmenas.tipos.${colmena.tipo}`, { defaultValue: colmena.tipo })}
                </span>
              )}
              {colmena.zona_nombre && <span style={S.metaChip}>📍 {colmena.zona_nombre}</span>}
              {colmena.fecha_instalacion && (
                <span style={S.metaChip}>📅 {t('ficha.installedOn')}: {fmtFechaSolo(colmena.fecha_instalacion)}</span>
              )}
              {!colmena.activa && <span style={{ ...S.metaChip, color: '#C0392B' }}>{t('common.inactive')}</span>}
            </div>
          </div>
          <div style={{ ...S.semaforoLarge, background: sem.bg, color: sem.color }}>
            {sem.icon} {t(sem.labelKey)}
          </div>
        </div>
        <div style={S.razonesBox}>
          {razones.length === 0 ? (
            <span style={{ fontSize: 13, color: '#4A7C59' }}>✓ {t('ficha.noAlerts')}</span>
          ) : (
            <>
              <span style={{ fontSize: 12, color: '#8B6355', fontWeight: 600 }}>{t('ficha.reasonsLabel')}:</span>
              {razones.map(k => (
                <span key={k} style={S.razonChip}>{t('estados.' + k, { defaultValue: k })}</span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* 2. Panel de estados */}
      <div style={S.card}>
        <div style={S.cardTitle}>📋 {t('ficha.statesTitle')}</div>
        <p style={S.cardHint}>{t('ficha.statesHint')}</p>
        {estados.length === 0 ? (
          <div style={S.empty}>{t('ficha.statesEmpty')}</div>
        ) : (
          CAT_ORDER.map(cat => {
            const items = grouped[cat];
            if (!items || items.length === 0) return null;
            const cc = CAT_COLORS[cat];
            return (
              <div key={cat} style={{ marginBottom: 14 }}>
                <div style={{ ...S.catLabel, background: cc.bg, color: cc.color, borderColor: cc.border }}>
                  {t('estado.cat' + cat.charAt(0).toUpperCase() + cat.slice(1))}
                </div>
                <div style={S.checkList}>
                  {items.map(e => (
                    <label key={e.key} style={S.checkItem}>
                      <input type="checkbox" checked={e.valor}
                        onChange={() => toggleEstado(e.key, e.valor)}
                        style={S.checkbox} />
                      <span style={{ flex: 1, fontSize: 14, color: e.valor ? '#2C1A0E' : '#8B6355' }}>
                        {t('estados.' + e.key, { defaultValue: e.label })}
                      </span>
                      {e.valor && cat === 'alerta' && (
                        <span style={{ fontSize: 11, color: '#C0392B', fontWeight: 500 }}>{t('estado.alertActive')}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 3. Varroa */}
      <div style={S.card}>
        <div style={S.cardTitle}>🔬 {t('ficha.varroaTitle')}</div>
        {!ultimoVarroa ? (
          <div style={S.empty}>{t('ficha.varroaEmpty')}</div>
        ) : (
          <>
            <div style={{ ...S.varroaUltimo, background: nivelColor(ultimoVarroa.nivel).bg }}>
              <div>
                <div style={{ fontSize: 12, color: '#8B6355' }}>{t('ficha.varroaLast')}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: nivelColor(ultimoVarroa.nivel).color, lineHeight: 1.2 }}>
                  {ultimoVarroa.valor} <span style={{ fontSize: 15, fontWeight: 500 }}>{unidadVarroa(ultimoVarroa.unidad)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#8B6355', marginTop: 2 }}>
                  {t(`varroa.metodoCorto_${ultimoVarroa.metodo}`, { defaultValue: ultimoVarroa.metodo })} · {fmtFecha(ultimoVarroa.fecha)}
                </div>
              </div>
              {ultimoVarroa.nivel && (
                <span style={{ ...S.nivelBadge, background: 'white', color: nivelColor(ultimoVarroa.nivel).color }}>
                  {t(`varroa.nivel_${ultimoVarroa.nivel}`)}
                </span>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={S.sectionLabel}>{t('ficha.varroaTrend')}</div>
              {anterioresVarroa.length === 0 ? (
                <div style={{ fontSize: 13, color: '#8B6355' }}>{t('ficha.varroaNoPrev')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {anterioresVarroa.map(v => {
                    const nc = nivelColor(v.nivel);
                    const pct = Math.max(4, Math.round((v.valor / maxVarroa) * 100));
                    return (
                      <div key={v.evento_id} style={S.varroaRow}>
                        <span style={{ fontSize: 12, color: '#8B6355', width: 92, flexShrink: 0 }}>{fmtFecha(v.fecha)}</span>
                        <div style={S.barTrack}>
                          <div style={{ ...S.barFill, width: `${pct}%`, background: nc.color }} />
                        </div>
                        <span style={{ fontSize: 12, color: nc.color, fontWeight: 600, width: 84, textAlign: 'right', flexShrink: 0 }}>
                          {v.valor} {unidadVarroa(v.unidad)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 4. Producción */}
      <div style={S.card}>
        <div style={S.cardTitle}>🍯 {t('ficha.prodTitle')}</div>
        {aniosProd.length === 0 ? (
          <div style={S.empty}>{t('ficha.prodEmpty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, textAlign: 'left' }}>{t('ficha.prodYear')}</th>
                  <th style={S.th}>{t('ficha.prodKg')}</th>
                  <th style={S.th}>{t('ficha.prodDiff')}</th>
                </tr>
              </thead>
              <tbody>
                {aniosProd.map(a => {
                  const d = prodColmena.por_anio[String(a)];
                  const prev = prodColmena.por_anio[String(a - 1)];
                  const diff = prev ? Math.round((d.kg - prev.kg) * 100) / 100 : null;
                  return (
                    <tr key={a}>
                      <td style={{ ...S.td, textAlign: 'left', fontWeight: 500 }}>{a}</td>
                      <td style={S.td}>
                        {d.kg} kg
                        {d.ceros_con_motivo > 0 && (
                          <span style={S.zeroBadge}>ⓞ {t('ficha.prodZeroReason', { count: d.ceros_con_motivo })}</span>
                        )}
                      </td>
                      <td style={{ ...S.td, fontWeight: 600, color: diff == null ? '#C0A090' : diff >= 0 ? '#1E8449' : '#C0392B' }}>
                        {diff == null ? '—' : `${diff >= 0 ? '+' : ''}${diff} kg`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 5. Tratamientos */}
      <div style={S.card}>
        <div style={S.cardTitle}>💊 {t('ficha.tratTitle')}</div>
        {tratTruncado && (
          <div style={{ background: '#FDF6E3', border: '1.5px solid #FAE5A0', color: '#7D6608', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 10, lineHeight: 1.5 }} role="alert">
            ⚠️ {t('ficha.tratTruncado')}
          </div>
        )}
        {tratamientos.length === 0 ? (
          <div style={S.empty}>{t('ficha.tratEmpty')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tratamientos.map(e => {
              const td = e.tratamiento_detalle;
              const nombre = !td
                ? e.titulo
                : td.sustancia === 'otro'
                  ? (td.sustancia_otro || t('tratamiento.sust_otro'))
                  : t(`tratamiento.sust_${td.sustancia}`, { defaultValue: e.titulo });
              return (
                <div key={e.id} style={S.tratRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{nombre}</div>
                    <div style={{ fontSize: 12, color: '#8B6355', marginTop: 2 }}>
                      {fmtFecha(e.fecha || e.created_at)}
                      {td?.dosis && <span> · {td.dosis}</span>}
                    </div>
                  </div>
                  {td?.apto_bio === true && <span style={{ ...S.chip, background: '#EAF7EE', color: '#1E8449' }}>🌿 {t('tratamiento.badgeBio')}</span>}
                  {td?.apto_bio === false && <span style={{ ...S.chip, background: '#FDECEA', color: '#C0392B' }}>⚠️ {t('tratamiento.badgeNoBio')}</span>}
                  {(!td || td.apto_bio == null) && <span style={{ ...S.chip, background: '#FDF6E3', color: '#7D6608' }}>❔ {t('tratamiento.badgeVerificar')}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 6. Historial */}
      <div style={S.card}>
        <div style={S.cardTitle}>📓 {t('ficha.historyTitle')}</div>
        <p style={S.cardHint}>{t('ficha.historyHint')}</p>
        {historial.length === 0 ? (
          <div style={S.empty}>{t('ficha.historyEmpty')}</div>
        ) : (
          <div>
            {historial.map((item, i) => {
              const info = TIPO_INFO[item.subtipo] || TIPO_INFO.otro;
              const fecha = new Date(item.fecha);
              return (
                <div key={i} style={S.timelineItem}>
                  <div style={S.timelineLine}>
                    <div style={{ ...S.timelineDot, background: info.color }}>
                      <span style={{ fontSize: 12 }}>{info.icon}</span>
                    </div>
                    {i < historial.length - 1 && <div style={S.timelineConnector} />}
                  </div>
                  <div style={S.timelineContent}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{item.titulo}</div>
                      <div style={{ fontSize: 11, color: '#C0A090', flexShrink: 0, marginLeft: 12 }}>
                        {fecha.toLocaleDateString(lang, { day: '2-digit', month: 'short' })}
                      </div>
                    </div>
                    {item.descripcion && (
                      <div style={{ fontSize: 12, color: '#8B6355', marginTop: 3 }}>{item.descripcion}</div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span style={{ ...S.tipoBadge, background: info.color + '18', color: info.color }}>{info.label}</span>
                      {item.subtipo === 'alza' && item.alza_detalle && (() => {
                        const ad = item.alza_detalle;
                        const isCol = ad.accion === 'colocada';
                        const s = ad.cantidad_alzas !== 1 ? 's' : '';
                        const sp = ad.cantidad_alzas !== 1 ? 's' : '';
                        return (
                          <span style={{ ...S.tipoBadge, background: isCol ? '#EDE8F8' : '#F0EDE9', color: isCol ? '#6B4FA0' : '#5C3D2E' }}>
                            {t(isCol ? 'timeline.alzaChipPlaced' : 'timeline.alzaChipRemoved', { count: ad.cantidad_alzas, s, sp })}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 7 y 8. Salidas */}
      <div style={S.acciones}>
        <Link to={`/bitacora?colmena=${colmena.id}`} className="btn btn-primary">
          {t('ficha.seeInBitacora')}
        </Link>
        <Link to={`/bitacora?colmena=${colmena.id}&nuevo=1`} className="btn btn-ghost">
          {t('ficha.registerEvent')}
        </Link>
      </div>
    </div>
  );
}

const S = {
  card: { background: '#FEFCF8', borderRadius: 20, boxShadow: '0 2px 12px rgba(44,26,14,0.10)', border: '1px solid rgba(232,146,42,0.12)', padding: 20, marginBottom: 16 },
  headerRow: { display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' },
  title: { fontSize: 24, fontFamily: "'DM Sans', sans-serif", margin: 0 },
  metaRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 },
  metaChip: { fontSize: 12, color: '#5C3D2E', background: '#F8F4EF', padding: '3px 10px', borderRadius: 20 },
  semaforoLarge: { padding: '6px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' },
  razonesBox: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #F0EDE9' },
  razonChip: { fontSize: 12, fontWeight: 500, background: '#FDECEA', color: '#C0392B', padding: '3px 10px', borderRadius: 20 },
  cardTitle: { fontSize: 16, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#2C1A0E', marginBottom: 10 },
  cardHint: { fontSize: 12, color: '#8B6355', marginTop: -6, marginBottom: 12 },
  sectionLabel: { fontSize: 12, fontWeight: 600, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  empty: { fontSize: 13, color: '#8B6355', background: '#F8F4EF', borderRadius: 12, padding: '14px 16px', lineHeight: 1.5 },
  catLabel: { display: 'inline-block', padding: '4px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 8, border: '1px solid' },
  checkList: { display: 'flex', flexDirection: 'column', gap: 2 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', background: '#F8F4EF' },
  checkbox: { width: 18, height: 18, accentColor: '#E8922A', cursor: 'pointer' },
  varroaUltimo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderRadius: 14, padding: '14px 16px', flexWrap: 'wrap' },
  nivelBadge: { padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  varroaRow: { display: 'flex', alignItems: 'center', gap: 10 },
  barTrack: { flex: 1, minWidth: 0, height: 8, background: '#F0EDE9', borderRadius: 6, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#8B6355', fontWeight: 600, borderBottom: '1px solid #F0EDE9', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #F8F4EF', whiteSpace: 'nowrap' },
  zeroBadge: { marginLeft: 6, fontSize: 11, color: '#8B6355' },
  tratRow: { display: 'flex', alignItems: 'center', gap: 10, background: '#F8F4EF', borderRadius: 12, padding: '10px 14px', flexWrap: 'wrap' },
  chip: { fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' },
  timelineItem: { display: 'flex', gap: 14 },
  timelineLine: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
  timelineDot: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 },
  timelineConnector: { width: 2, flex: 1, background: '#F0EDE9', minHeight: 16, margin: '4px 0' },
  timelineContent: { flex: 1, minWidth: 0, background: 'white', borderRadius: 12, padding: '10px 14px', marginBottom: 8, boxShadow: '0 1px 6px rgba(44,26,14,0.06)' },
  tipoBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, marginTop: 6 },
  acciones: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
};
